import { useState } from "react";
import { Box, Button, Chip, Collapse, Dialog, DialogContent, Link, Typography } from "@mui/material";
import { CheckCircle, Cancel, ExpandMore, ExpandLess, Accessibility, Compare } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../services/api";

// What an API request, visual check or accessibility check found on a run
// (step result metadata.api / .visual / .accessibility, from executor/src/checks).

export const CHECK_STEP_LABELS = {
	apiRequest: "API request",
	visualCheck: "Visual check",
	accessibilityCheck: "Accessibility check",
};

/** One line saying what a check step does, for step lists. */
export function checkStepSummary(step) {
	switch (step?.type) {
		case "apiRequest":
			return `${step.request?.method || "GET"} ${step.request?.url || ""}`;
		case "visualCheck":
			return step.selector ? `Element ${step.selector}` : step.visual?.fullPage ? "Full page" : "Visible page";
		case "accessibilityCheck":
			return `WCAG ${step.accessibility?.level || "AA"}, fail on ${step.accessibility?.failOn || "serious"} or worse${step.selector ? ` · ${step.selector}` : ""}`;
		default:
			return null;
	}
}

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12.5 };

const Code = ({ children, maxHeight = 280 }) => (
	<Box
		component="pre"
		sx={{
			...mono,
			m: 0,
			p: 1.25,
			maxHeight,
			overflow: "auto",
			borderRadius: 1,
			border: 1,
			borderColor: "divider",
			bgcolor: "background.paper",
			color: "text.primary",
			whiteSpace: "pre-wrap",
			wordBreak: "break-word",
		}}
	>
		{children}
	</Box>
);

const Label = ({ children }) => (
	<Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mt: 1.5, mb: 0.5 }}>
		{children}
	</Typography>
);

function prettyBody(body) {
	if (!body) return "(empty)";
	try {
		return JSON.stringify(JSON.parse(body), null, 2);
	} catch {
		return body;
	}
}

const statusColor = (status) => (status >= 500 ? "error" : status >= 400 ? "warning" : status >= 300 ? "info" : "success");

function ApiDetails({ api: result }) {
	const [showHeaders, setShowHeaders] = useState(false);
	const { request, response, checks = [], saved = {} } = result;
	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
				<Typography sx={{ ...mono, fontWeight: 600 }}>
					{request?.method} {request?.url}
				</Typography>
				{response && (
					<>
						<Chip size="small" color={statusColor(response.status)} label={`${response.status} ${response.statusText || ""}`.trim()} />
						<Typography variant="caption" color="text.secondary">
							{response.timeMs} ms
						</Typography>
					</>
				)}
			</Box>
			{checks.length > 0 && (
				<>
					<Label>Checks</Label>
					{checks.map((c, i) => (
						<Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.25 }}>
							{c.passed ? (
								<CheckCircle sx={{ fontSize: 16, mt: "2px" }} color="success" />
							) : (
								<Cancel sx={{ fontSize: 16, mt: "2px" }} color="error" />
							)}
							<Typography variant="body2">
								{c.check}
								{!c.passed && (
									<Typography component="span" variant="body2" color="text.secondary">
										{" "}
										— got <Box component="span" sx={mono}>{c.actual}</Box>
									</Typography>
								)}
							</Typography>
						</Box>
					))}
				</>
			)}
			{Object.keys(saved).length > 0 && (
				<>
					<Label>Saved for later requests</Label>
					{Object.entries(saved).map(([k, v]) => (
						<Typography key={k} variant="body2" sx={mono}>
							{`{{${k}}}`} = {v}
						</Typography>
					))}
				</>
			)}
			{response && (
				<>
					<Label>Response body</Label>
					<Code>{prettyBody(response.body)}</Code>
					<Button
						size="small"
						onClick={() => setShowHeaders((s) => !s)}
						endIcon={showHeaders ? <ExpandLess /> : <ExpandMore />}
						sx={{ mt: 0.5, px: 0.5 }}
					>
						Response headers
					</Button>
					<Collapse in={showHeaders}>
						<Code maxHeight={200}>
							{Object.entries(response.headers || {})
								.map(([k, v]) => `${k}: ${v}`)
								.join("\n")}
						</Code>
					</Collapse>
				</>
			)}
		</Box>
	);
}

function Shot({ title, b64, onOpen }) {
	if (!b64) return null;
	const src = `data:image/png;base64,${b64}`;
	return (
		<Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
			<Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
				{title}
			</Typography>
			<Box
				component="img"
				src={src}
				alt={title}
				onClick={() => onOpen({ title, src })}
				sx={{ width: "100%", maxHeight: 260, objectFit: "contain", border: 1, borderColor: "divider", borderRadius: 1, cursor: "zoom-in", bgcolor: "background.paper" }}
			/>
		</Box>
	);
}

function VisualDetails({ visual, executionId, stepIndex }) {
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(null);
	const [accepted, setAccepted] = useState(!!visual.accepted);
	const [saving, setSaving] = useState(false);
	const mismatch = visual.status === "mismatch" || visual.status === "size-mismatch";

	const accept = async () => {
		setSaving(true);
		try {
			await api.post(`/executions/${executionId}/steps/${stepIndex}/accept-baseline`);
			setAccepted(true);
			enqueueSnackbar("Saved as the new baseline: the next run compares against it", { variant: "success" });
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Couldn't save the baseline", { variant: "error" });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
				<Compare fontSize="small" color="action" />
				<Typography variant="body2">
					{visual.status === "new" && "No baseline yet: this screenshot is now the baseline."}
					{visual.status === "match" && `Matches the baseline (${visual.diffPercent}% of pixels differ, ${visual.maxDiffPercent}% allowed).`}
					{visual.status === "mismatch" && `${visual.diffPercent}% of pixels differ from the baseline (${visual.maxDiffPercent}% allowed). Changes are shown in red.`}
					{visual.status === "size-mismatch" && "The screenshot's size differs from the baseline's."}
				</Typography>
			</Box>
			{(visual.status === "new" || mismatch) && (
				<Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
					<Shot title="Baseline" b64={visual.baseline} onOpen={setOpen} />
					<Shot title={visual.status === "new" ? "Saved as baseline" : "This run"} b64={visual.actual} onOpen={setOpen} />
					<Shot title="Difference" b64={visual.diff} onOpen={setOpen} />
				</Box>
			)}
			{mismatch && executionId != null && (
				<Box sx={{ mt: 1.5 }}>
					{accepted ? (
						<Chip size="small" color="success" variant="outlined" label="Accepted as the new baseline" />
					) : (
						<Button size="small" variant="outlined" onClick={accept} disabled={saving}>
							{saving ? "Saving…" : "Accept as new baseline"}
						</Button>
					)}
				</Box>
			)}
			<Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="xl">
				<DialogContent>
					<Typography variant="subtitle2" gutterBottom>
						{open?.title}
					</Typography>
					{open && <Box component="img" src={open.src} alt={open.title} sx={{ maxWidth: "100%" }} />}
				</DialogContent>
			</Dialog>
		</Box>
	);
}

const IMPACT_COLORS = { critical: "error", serious: "error", moderate: "warning", minor: "default" };

function AccessibilityDetails({ accessibility: a11y }) {
	const violations = a11y.violations || [];
	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Accessibility fontSize="small" color="action" />
				<Typography variant="body2">
					{violations.length === 0
						? `No WCAG ${a11y.level} violations found.`
						: `${violations.length} violation${violations.length > 1 ? "s" : ""} (WCAG ${a11y.level}); ${a11y.failing} at "${a11y.failOn}" or worse${a11y.failing ? " fail the step" : ""}.`}
				</Typography>
			</Box>
			{violations.map((v) => (
				<Box key={v.id} sx={{ mt: 1.25, pl: 1.25, borderLeft: 2, borderColor: v.failing ? "error.main" : "divider" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
						<Chip size="small" label={v.impact} color={IMPACT_COLORS[v.impact] || "default"} variant={v.failing ? "filled" : "outlined"} />
						<Typography variant="body2" fontWeight={600}>
							{v.help}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{v.elements} element{v.elements > 1 ? "s" : ""} ·{" "}
							<Link href={v.helpUrl} target="_blank" rel="noopener noreferrer">
								{v.id}
							</Link>
						</Typography>
					</Box>
					{v.summary && (
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
							{v.summary}
						</Typography>
					)}
					{v.targets?.map((t, i) => (
						<Typography key={i} variant="caption" display="block" sx={{ ...mono, fontSize: 11.5, color: "text.secondary" }}>
							{t}
						</Typography>
					))}
				</Box>
			))}
		</Box>
	);
}

/**
 * The details of a check step's result, or null for other steps.
 * stepIndex: the step result's index (for "Accept as new baseline").
 */
export default function StepCheckDetails({ stepResult, executionId, stepIndex }) {
	const m = stepResult?.metadata;
	if (!m) return null;
	return (
		<Box sx={{ mt: 1.5 }}>
			{m.api && <ApiDetails api={m.api} />}
			{m.visual && <VisualDetails visual={m.visual} executionId={executionId} stepIndex={stepIndex} />}
			{m.accessibility && <AccessibilityDetails accessibility={m.accessibility} />}
		</Box>
	);
}
